import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import moment from 'moment';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { logsService } from '../../services/logs.service';

const MySwal = withReactContent(Swal);

/* ---------- styles ---------- */
const sectionCard = { background: "#fff", borderRadius: 14, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 2 };
const valueStyle = { fontSize: 14, fontWeight: 600, color: "#1a202c", wordBreak: "break-word" };
const monoStyle = { fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 12 };
const codeBlock = {
  ...monoStyle, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
  background: "#1e1b3a", color: "#e4e1ff", borderRadius: 10, padding: 14, overflow: "auto"
};
const pill = (bg, color) => ({
  display: "inline-block", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12,
  background: bg, color, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap"
});
const btnLight = { background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" };
const smallBtn = { ...btnLight, height: 28, fontSize: 12 };

const METHOD_COLORS = {
  GET: ["#e6f7f1", "#0f8a5f"],
  POST: ["#e8f0fe", "#0d6efd"],
  PUT: ["#fff4e6", "#e8590c"],
  PATCH: ["#f3ebfa", "#764ba2"],
  DELETE: ["#fdecec", "#d64545"],
};

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm:ss A") : "-");
const formatDuration = (ms) => (ms == null ? "-" : ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`);
const toJson = (v) => (typeof v === "string" ? v : JSON.stringify(v ?? null, null, 2));
const isEmpty = (v) => v === undefined || v === null || v === "" || (typeof v === "object" && Object.keys(v).length === 0);
const isSuccess = (code) => code >= 200 && code < 300;
const statusColor = (code) => (code >= 500 ? "#d64545" : code >= 400 ? "#e8590c" : "#0f8a5f");

const copyText = (text) => {
  navigator.clipboard?.writeText(text)
    .then(() => toast.success("Copied to clipboard"))
    .catch(() => toast.error("Could not copy"));
};

// Dark JSON box with Copy and (for long content) Expand/Collapse
const JsonBlock = ({ label, value }) => {
  const [expanded, setExpanded] = useState(false);
  const text = toJson(value);
  const lineCount = isEmpty(value) ? 0 : text.split("\n").length;
  const isLong = lineCount > 20;

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center justify-content-between mb-1 gap-2">
        <div style={labelStyle}>
          {label}
          {lineCount > 0 && <span style={{ textTransform: "none", letterSpacing: 0, color: "#a0a8b8" }}> · {lineCount} lines</span>}
        </div>
        {!isEmpty(value) && (
          <div className="d-flex gap-1">
            {isLong && (
              <button type="button" onClick={() => setExpanded(!expanded)} className="btn btn-sm d-flex align-items-center gap-1 px-2" style={smallBtn}>
                <i className={`bi ${expanded ? "bi-arrows-collapse" : "bi-arrows-expand"}`}></i> {expanded ? "Collapse" : "Expand"}
              </button>
            )}
            <button type="button" onClick={() => copyText(text)} className="btn btn-sm d-flex align-items-center gap-1 px-2" style={smallBtn}>
              <i className="bi bi-clipboard"></i> Copy
            </button>
          </div>
        )}
      </div>
      {isEmpty(value)
        ? <div style={{ fontSize: 13, color: "#8a9ab5" }}>Empty</div>
        : <pre style={{ ...codeBlock, maxHeight: expanded ? "none" : 360 }}>{text}</pre>}
    </div>
  );
};

const ServiceApiLogDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState(null);

  useEffect(() => {
    loadDetail();
  }, [id]);

  /* ---------- API: details ---------- */
  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await logsService.serviceApiDetails(id);
      if (res?.success) {
        setLog(res.resData);
      } else {
        setLog(null);
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- small render helpers ---------- */
  const sectionTitle = (icon, text, action) => (
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
      <h6 className="m-0 d-flex align-items-center gap-2 fw-bold" style={{ fontSize: 14, color: "#4b3f8f" }}>
        <i className={`bi ${icon}`} style={{ color: "#667eea" }}></i> {text}
      </h6>
      {action}
    </div>
  );
  const field = (label, value, mono) => (
    <div className="col-12 col-md-6 col-lg-4" key={label}>
      <div style={labelStyle}>{label}</div>
      <div style={mono ? { ...valueStyle, ...monoStyle, fontWeight: 600 } : valueStyle}>{isEmpty(value) ? "-" : value}</div>
    </div>
  );
  const methodPill = (m) => {
    const [bg, color] = METHOD_COLORS[m] || ["#f1f3f9", "#6b7a90"];
    return <span style={pill(bg, color)}>{m || "-"}</span>;
  };

  const headers = log?.request_headers || {};
  const ok = isSuccess(log?.response_status);

  return (
    <section className="section dashboard">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>Service API Log #{id}</h4>
          <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
            <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/logs" style={{ color: "#8a9ab5" }}>Logs</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/logs/service-api" style={{ color: "#8a9ab5" }}>Service API Logs</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <span className="fw-semibold" style={{ color: "#4a5568" }}>#{id}</span>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" onClick={loadDetail} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          <Link to="/logs/service-api" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Service API Logs
          </Link>
        </div>
      </div>

      {loading ? <ContentLoader loaderType="circleSpin" /> : !log ? (
        <div style={{ ...sectionCard, textAlign: "center", padding: 40 }}>
          <i className="bi bi-search" style={{ fontSize: 32, color: "#b4bdd0" }}></i>
          <div className="fw-semibold mt-2" style={{ color: "#4a5568" }}>Service API log not found</div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ ...sectionCard, borderLeft: `4px solid ${statusColor(log.response_status)}` }}>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
              <span style={ok ? pill("#e6f7f1", "#0f8a5f") : pill(log.response_status >= 500 ? "#fdecec" : "#fff4e6", statusColor(log.response_status))}>
                {ok ? "Success" : "Failed"}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: statusColor(log.response_status) }}>HTTP {log.response_status ?? "-"}</span>
              {log.aborted && <span style={pill("#fdecec", "#d64545")}>Aborted</span>}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {methodPill(log.http_method)}
              <span className="fw-bold" style={{ ...monoStyle, fontSize: 16, color: "#1a202c", wordBreak: "break-all" }}>{log.endpoint || "-"}</span>
            </div>
            <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <span><i className="bi bi-clock me-1"></i>{formatDate(log.created_at)}</span>
              <span><i className="bi bi-stopwatch me-1"></i>{formatDuration(log.duration_ms)}</span>
              <span><i className="bi bi-person-badge me-1"></i>{log.agent_code || "-"}</span>
            </div>
          </div>

          {/* Overview */}
          <div style={sectionCard}>
            {sectionTitle("bi-info-circle-fill", "Overview")}
            <div className="row g-3">
              {field("Request ID", log.request_id, true)}
              {field("Agent Code", log.agent_code)}
              {field("User ID", log.user_id)}
              {field("API Key Hint", log.api_key_hint, true)}
              {field("IP Address", log.ip_address, true)}
              {field("Duration", formatDuration(log.duration_ms))}
            </div>
          </div>

          <div className="row g-3">
            {/* Request */}
            <div className="col-12 col-xl-6">
              <div style={{ ...sectionCard, height: "calc(100% - 16px)" }}>
                {sectionTitle("bi-box-arrow-in-right", "Request")}
                <div className="row g-3">
                  {field("Method", log.http_method)}
                  <div className="col-12">
                    <div style={labelStyle}>Endpoint</div>
                    <div style={{ ...valueStyle, ...monoStyle }}>{log.endpoint || "-"}</div>
                  </div>
                </div>
                <JsonBlock label="Query" value={log.request_query} />
                <JsonBlock label="Body" value={log.request_body} />
              </div>
            </div>

            {/* Response */}
            <div className="col-12 col-xl-6">
              <div style={{ ...sectionCard, height: "calc(100% - 16px)" }}>
                {sectionTitle("bi-box-arrow-right", "Response")}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div style={labelStyle}>Status</div>
                    <div style={{ ...valueStyle, color: statusColor(log.response_status) }}>{log.response_status ?? "-"}</div>
                  </div>
                  {field("Aborted", log.aborted ? "Yes" : "No")}
                </div>
                <JsonBlock label="Body" value={log.response_body} />
              </div>
            </div>
          </div>

          {/* Headers */}
          <div style={sectionCard}>
            {sectionTitle("bi-list-columns-reverse", `Request Headers (${Object.keys(headers).length})`, !isEmpty(headers) && (
              <button type="button" onClick={() => copyText(toJson(headers))} className="btn btn-sm d-flex align-items-center gap-1 px-2" style={smallBtn}>
                <i className="bi bi-clipboard"></i> Copy
              </button>
            ))}
            {isEmpty(headers) ? <div style={{ fontSize: 13, color: "#8a9ab5" }}>Empty</div> : (
              <div className="table-responsive" style={{ border: "1px solid #eef1f6", borderRadius: 10 }}>
                <table className="table table-sm align-middle mb-0">
                  <tbody>
                    {Object.entries(headers).map(([key, value]) => (
                      <tr key={key}>
                        <td style={{ ...monoStyle, color: "#6b7a90", whiteSpace: "nowrap", width: "1%", padding: "8px 12px", background: "#f8f9fe" }}>{key}</td>
                        <td style={{ ...monoStyle, color: "#1a202c", wordBreak: "break-all", padding: "8px 12px" }}>{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default ServiceApiLogDetail;
